'use client'

import { AnimatedHeight } from '@fukku/animated-height/AnimatedHeight'
import { Dialog, type DialogAPI } from '@fukku/dialog/Dialog'
import { useClosingAnimation } from '@fukku/dialog/hooks/useClosingAnimation'
import type { DialogProps } from '@fukku/dialog/types/props'
import { cx } from 'classix'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { DialogContextProvider, DialogType } from '../hooks/useDialogContext'
import { apiFactory } from '../utils/apiFactory'
import styles from './BottomSheet.module.css'
import { useDraggableBottomSheet } from './hooks/useDraggableBottomSheet'

const MOTION_SHORT1 = 100

export function BottomSheetComponent(
	{
		children,
		preventClose = false,
		keepContent = false,
		title,
		onClose,
		onOpen,
		className,
		'data-testid': dataTestId,
		id,
	}: DialogProps,
	ref: React.Ref<DialogAPI>
) {
	const { isTouchable } = useResponsive()
	const dialogRef = useRef<HTMLDialogElement>(null)
	const draggableRef = useRef<HTMLDivElement>(null)

	const { opened, closing, setOpened } = useClosingAnimation(dialogRef)

	const { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel, onScroll } =
		useDraggableBottomSheet({
			dialogRef,
			draggableRef,
			opened,
			setOpened,
		})

	const api = apiFactory(setOpened, dialogRef.current)

	useImperativeHandle(ref, () => api, [api])

	return (
		<DialogContextProvider
			type={DialogType.BottomSheet}
			api={api}
			preventClose={preventClose}
		>
			<Dialog
				title={title}
				className={cx(styles.bottom, closing && styles.closing, className)}
				ref={dialogRef}
				preventClose={preventClose}
				keepContent={keepContent}
				setOpened={setOpened}
				opened={opened}
				onOpen={onOpen}
				onClose={onClose}
				data-testid={dataTestId}
				id={id}
			>
				<div
					ref={draggableRef}
					data-draggable
					className={cx(styles.draggable, isTouchable && styles.touchable)}
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					onTouchEnd={onTouchEnd}
					onTouchCancel={onTouchCancel}
					onScroll={onScroll}
				>
					<AnimatedHeight delay={MOTION_SHORT1}>{children}</AnimatedHeight>
				</div>
			</Dialog>
		</DialogContextProvider>
	)
}

export const BottomSheet = forwardRef(BottomSheetComponent)
